import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Check,
  ChevronRight,
  FileSpreadsheet,
  Loader2,
  Lock,
  Search,
  Sparkles,
  Upload,
  X,
  Zap,
} from "lucide-react";

function Spinner({ className = "h-4 w-4" }) {
  return <Loader2 className={`${className} animate-spin`} strokeWidth={2} />;
}

function bankInitials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "BK";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

/**
 * Enterprise stepped dialog:
 * 1) Choose payment method (bank file vs API coming soon)
 * 2) Select bank + format → generate & download
 */
export default function GenerateDisbursementDialog({
  batch,
  banks = [],
  formats = [],
  loadingOptions = false,
  submitting = false,
  validationError = null,
  onClose,
  onGenerate,
}) {
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState(null); // bank_file | bank_api
  const [bankCode, setBankCode] = useState("");
  const [exportFormat, setExportFormat] = useState("excel");
  const [bankQuery, setBankQuery] = useState("");
  const [bankMenuOpen, setBankMenuOpen] = useState(false);
  const searchRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!banks.length) return;
    // Prefer a real bank over DEFAULT for first selection.
    const preferred =
      banks.find((b) => b.bank_code === "AXIS") ||
      banks.find((b) => !b.is_generic) ||
      banks[0];
    setBankCode((prev) => prev || preferred?.bank_code || "DEFAULT");
  }, [banks]);

  useEffect(() => {
    if (step === 2 && searchRef.current) {
      const t = setTimeout(() => searchRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [step]);

  useEffect(() => {
    if (!bankMenuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      setBankMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [bankMenuOpen]);

  const filteredBanks = useMemo(() => {
    const q = bankQuery.trim().toLowerCase();
    if (!q) return banks;
    return banks.filter(
      (b) =>
        b.bank_name?.toLowerCase().includes(q) ||
        b.bank_code?.toLowerCase().includes(q) ||
        b.layout_summary?.toLowerCase().includes(q)
    );
  }, [banks, bankQuery]);

  const selectedBank = useMemo(
    () => banks.find((b) => b.bank_code === bankCode) || null,
    [banks, bankCode]
  );

  const formatOptions = formats.length
    ? formats
    : [
        { id: "csv", label: "CSV" },
        { id: "excel", label: "Excel (XLSX)" },
        { id: "pdf", label: "PDF" },
      ];

  const close = () => {
    if (submitting) return;
    onClose?.();
  };

  const goMethod = (id) => {
    if (id === "bank_api") {
      setMethod("bank_api");
      return;
    }
    setMethod("bank_file");
    setStep(2);
  };

  const handleGenerate = () => {
    if (!bankCode || submitting) return;
    onGenerate?.({ bankCode, exportFormat });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-[3px]">
      <div
        className="relative w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-2xl shadow-slate-900/20 ring-1 ring-slate-200/80"
        role="dialog"
        aria-modal="true"
        aria-labelledby="disbursement-dialog-title"
      >
        {/* Header */}
        <div className="relative overflow-hidden border-b border-slate-100 bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 px-6 pb-5 pt-5 text-white">
          <div className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full bg-sky-400/20 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-10 left-10 h-28 w-28 rounded-full bg-indigo-400/15 blur-2xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-sky-100 ring-1 ring-white/15">
                <Sparkles className="h-3 w-3" />
                Payroll Disbursement
              </div>
              <h2 id="disbursement-dialog-title" className="text-xl font-semibold tracking-tight">
                Generate Disbursement
              </h2>
              <p className="mt-1 text-sm text-slate-300">
                Batch{" "}
                <span className="font-mono font-medium text-white">{batch?.batch_ref}</span>
                {" · "}
                Choose how salary payments should be processed.
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              className="rounded-xl p-1.5 text-white/70 transition hover:bg-white/10 hover:text-white"
              disabled={submitting}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Steps */}
          <div className="relative mt-5 flex items-center gap-2 text-xs font-medium">
            <div
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ring-1 ${
                step === 1
                  ? "bg-white text-slate-900 ring-white"
                  : "bg-white/10 text-sky-100 ring-white/15"
              }`}
            >
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-sky-600 text-[10px] text-white">
                1
              </span>
              Payment method
            </div>
            <ChevronRight className="h-3.5 w-3.5 text-white/40" />
            <div
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ring-1 ${
                step === 2
                  ? "bg-white text-slate-900 ring-white"
                  : "bg-white/10 text-sky-100/80 ring-white/10"
              }`}
            >
              <span
                className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${
                  step === 2 ? "bg-sky-600 text-white" : "bg-white/20 text-white/80"
                }`}
              >
                2
              </span>
              Select bank
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          {step === 1 ? (
            <div className="space-y-3 animate-[fadeIn_0.25s_ease]">
              <p className="text-sm font-medium text-slate-800">
                How would you like to process salary payments?
              </p>

              <button
                type="button"
                onClick={() => goMethod("bank_file")}
                className="group flex w-full items-start gap-4 rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-sky-50/60 p-4 text-left transition hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-md hover:shadow-sky-100"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-600 text-white shadow-sm shadow-sky-600/30">
                  <Upload className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-slate-900">Generate Bank Upload File</div>
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 ring-1 ring-emerald-200">
                      Default
                    </span>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">
                    Generate a bank-specific salary upload file that can be uploaded into the
                    selected bank&apos;s Corporate Internet Banking portal.
                  </p>
                </div>
                <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-slate-300 transition group-hover:text-sky-600" />
              </button>

              <button
                type="button"
                onClick={() => goMethod("bank_api")}
                className={`flex w-full items-start gap-4 rounded-2xl border p-4 text-left transition ${
                  method === "bank_api"
                    ? "border-violet-300 bg-violet-50/70 ring-2 ring-violet-100"
                    : "border-slate-200 bg-slate-50/80 hover:border-violet-200"
                }`}
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
                  <Zap className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-semibold text-slate-900">Direct Bank API</div>
                    <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-800 ring-1 ring-violet-200">
                      Enterprise
                    </span>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">
                    Send salary payments directly to the bank using secure API integration.
                  </p>
                  {method === "bank_api" ? (
                    <div className="mt-3 flex items-start gap-2 rounded-xl border border-violet-200 bg-white/80 px-3 py-2.5 text-sm text-violet-950">
                      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-violet-600" />
                      <div>
                        <div className="font-semibold">Coming Soon – Enterprise Banking Integration</div>
                        <p className="mt-0.5 text-xs leading-relaxed text-violet-800/90">
                          OAuth / API keys, maker-checker, payment status tracking, and webhook
                          reconciliation will plug into this path without changing bank-file
                          generation.
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
              </button>
            </div>
          ) : (
            <div className="space-y-4 animate-[fadeIn_0.25s_ease]">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 transition hover:text-slate-800"
                disabled={submitting}
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to payment method
              </button>

              {validationError ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-950">
                  <div className="font-semibold">{validationError.message}</div>
                  {validationError.hint ? (
                    <div className="mt-1 text-amber-900/90">{validationError.hint}</div>
                  ) : null}
                  {validationError.issues?.length ? (
                    <ul className="mt-2 list-disc space-y-1 pl-4 text-xs">
                      {validationError.issues.slice(0, 5).map((iss, i) => (
                        <li key={i}>
                          {iss.employee_code || iss.employee_name || iss.employee_id}:{" "}
                          {iss.title || iss.detail}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <Link
                    to="/employees"
                    className="mt-2 inline-block text-xs font-semibold underline underline-offset-2"
                  >
                    Open People Hub → Banking
                  </Link>
                </div>
              ) : null}

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Select bank
                </label>
                <div className="relative" ref={menuRef}>
                  <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm focus-within:border-sky-300 focus-within:ring-2 focus-within:ring-sky-100">
                    <Search className="h-4 w-4 shrink-0 text-slate-400" />
                    <input
                      ref={searchRef}
                      value={bankMenuOpen ? bankQuery : selectedBank?.bank_name || ""}
                      onChange={(e) => {
                        setBankQuery(e.target.value);
                        setBankMenuOpen(true);
                      }}
                      onFocus={() => {
                        setBankQuery("");
                        setBankMenuOpen(true);
                      }}
                      placeholder="Search Axis, HDFC, SBI…"
                      className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
                      disabled={loadingOptions || submitting}
                    />
                    <Building2 className="h-4 w-4 shrink-0 text-slate-300" />
                  </div>

                  {bankMenuOpen ? (
                    <div className="absolute z-20 mt-1.5 max-h-56 w-full overflow-auto rounded-2xl border border-slate-200 bg-white py-1 shadow-xl shadow-slate-900/10">
                      {loadingOptions ? (
                        <div className="flex items-center gap-2 px-3 py-3 text-sm text-slate-500">
                          <Spinner /> Loading banks…
                        </div>
                      ) : filteredBanks.length ? (
                        filteredBanks.map((b) => {
                          const active = b.bank_code === bankCode;
                          return (
                            <button
                              key={b.bank_code}
                              type="button"
                              onClick={() => {
                                setBankCode(b.bank_code);
                                setBankQuery("");
                                setBankMenuOpen(false);
                                const preferred = b.preferred_extensions || [];
                                if (preferred.includes("xlsx") || preferred.includes("excel")) {
                                  setExportFormat("excel");
                                } else if (preferred.includes("csv")) {
                                  setExportFormat("csv");
                                }
                              }}
                              className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition hover:bg-sky-50 ${
                                active ? "bg-sky-50/80" : ""
                              }`}
                            >
                              <div
                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[11px] font-bold ${
                                  active
                                    ? "bg-sky-600 text-white"
                                    : "bg-slate-100 text-slate-600"
                                }`}
                              >
                                {bankInitials(b.bank_name)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-medium text-slate-900">
                                  {b.bank_name}
                                </div>
                                <div className="truncate text-xs text-slate-500">
                                  {b.layout_summary || b.bank_code}
                                </div>
                              </div>
                              {active ? <Check className="h-4 w-4 shrink-0 text-sky-600" /> : null}
                            </button>
                          );
                        })
                      ) : (
                        <div className="px-3 py-3 text-sm text-slate-500">No banks match your search.</div>
                      )}
                    </div>
                  ) : null}
                </div>

                {selectedBank ? (
                  <div className="mt-2.5 flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50/80 px-3.5 py-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-[11px] font-bold text-white">
                      {bankInitials(selectedBank.bank_name)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900">{selectedBank.bank_name}</div>
                      <p className="mt-0.5 text-xs leading-relaxed text-slate-600">
                        {selectedBank.layout_summary ||
                          "Uses this bank’s corporate salary upload column layout."}
                      </p>
                      {selectedBank.preferred_extensions?.length ? (
                        <div className="mt-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                          Typical file type: {selectedBank.preferred_extensions.join(" · ").toUpperCase()}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  File format
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {formatOptions.map((fmt) => {
                    const selected = exportFormat === fmt.id;
                    return (
                      <button
                        key={fmt.id}
                        type="button"
                        onClick={() => setExportFormat(fmt.id)}
                        disabled={submitting}
                        className={`rounded-xl border px-3 py-3 text-center text-sm font-medium transition ${
                          selected
                            ? "border-blue-600 bg-blue-50 text-blue-800 ring-2 ring-blue-200"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        {fmt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/70 px-6 py-4">
          <button
            type="button"
            onClick={close}
            className="rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-200/70 hover:text-slate-900"
            disabled={submitting}
          >
            Cancel
          </button>
          {step === 2 ? (
            <button
              type="button"
              onClick={handleGenerate}
              disabled={submitting || loadingOptions || !bankCode}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-blue-600/25 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? <Spinner /> : <FileSpreadsheet className="h-4 w-4" />}
              {submitting ? "Generating…" : "Generate & Download"}
            </button>
          ) : (
            <div className="text-xs text-slate-500">Select a payment method to continue</div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
